/**
 * pSEO URL Generator
 * Utility functions to generate search URLs for programmatic SEO
 */

import {
  PropertyType,
  ListingIntent,
} from '../../components/property/property-card/PropertyCardTypes';

/**
 * Generate pSEO URL from search parameters
 *
 * Examples:
 * generatepSEOUrl('rent', 'Dublin', 'pet-friendly')
 * → /rent/dublin/pet-friendly
 *
 * generatepSEOUrl('sale', 'Dublin 4', '3-bed')
 * → /sale/dublin-4/3-bed
 */
export const generatepSEOUrl = (
  intent: ListingIntent | 'Any',
  location?: string,
  ...filters: string[]
): string => {
  const parts: string[] = [];

  // Add intent (rent, sale, share)
  if (intent !== 'Any') {
    parts.push(slugify(intent));
  }

  // Add location
  if (location) {
    parts.push(slugify(location));
  }

  // Add filters
  filters.forEach((filter) => {
    if (filter) {
      parts.push(slugify(filter));
    }
  });

  if (parts.length === 0) {
    return '/browse';
  }

  return `/${parts.join('/')}`;
};

/**
 * Convert text to URL-friendly slug
 * E.g., "Pet-Friendly" → "pet-friendly", "Dublin 4" → "dublin-4"
 */
export const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[^\w-]/g, '') // Remove special characters
    .replace(/-+/g, '-'); // Replace multiple hyphens with single hyphen
};

/**
 * Popular pSEO URL templates for common searches
 * These can be used for sitemap generation, internal linking, etc.
 */
export const popularpSEOTemplates = [
  // Rent - Location only
  { url: '/rent/dublin', title: 'Properties for Rent in Dublin', volume: 9900 },
  { url: '/rent/cork', title: 'Properties for Rent in Cork', volume: 2200 },
  { url: '/rent/galway', title: 'Properties for Rent in Galway', volume: 1100 },
  { url: '/rent/limerick', title: 'Properties for Rent in Limerick', volume: 590 },
  { url: '/rent/waterford', title: 'Properties for Rent in Waterford', volume: 480 },
  { url: '/rent/dublin-1', title: 'Properties for Rent in Dublin 1', volume: 720 },
  { url: '/rent/dublin-2', title: 'Properties for Rent in Dublin 2', volume: 880 },
  { url: '/rent/dublin-4', title: 'Properties for Rent in Dublin 4', volume: 650 },

  // Rent - With property type
  { url: '/rent/dublin/apartment', title: 'Apartments for Rent in Dublin', volume: 5400 },
  { url: '/rent/dublin/house', title: 'Houses for Rent in Dublin', volume: 2900 },
  { url: '/rent/dublin/studio', title: 'Studios for Rent in Dublin', volume: 1400 },
  { url: '/rent/dublin/room', title: 'Rooms for Rent in Dublin', volume: 1800 },

  // Rent - With amenities
  {
    url: '/rent/dublin/pet-friendly',
    title: 'Pet-Friendly Properties for Rent in Dublin',
    volume: 2100,
  },
  {
    url: '/rent/dublin/parking',
    title: 'Properties with Parking for Rent in Dublin',
    volume: 1800,
  },
  { url: '/rent/dublin/furnished', title: 'Furnished Properties for Rent in Dublin', volume: 1600 },
  { url: '/rent/dublin/wifi', title: 'Properties with Wi-Fi for Rent in Dublin', volume: 920 },
  { url: '/rent/dublin/garden', title: 'Properties with Garden for Rent in Dublin', volume: 640 },

  // Rent - With bedrooms
  { url: '/rent/dublin/1-bed', title: '1 Bedroom Properties for Rent in Dublin', volume: 3600 },
  { url: '/rent/dublin/2-bed', title: '2 Bedroom Properties for Rent in Dublin', volume: 4400 },
  { url: '/rent/dublin/3-bed', title: '3 Bedroom Properties for Rent in Dublin', volume: 2800 },
  { url: '/rent/dublin/4-bed', title: '4 Bedroom Properties for Rent in Dublin', volume: 1200 },

  // Rent - With price range
  {
    url: '/rent/dublin/500-1000',
    title: 'Properties for Rent in Dublin €500-€1000/month',
    volume: 880,
  },
  {
    url: '/rent/dublin/1000-1500',
    title: 'Properties for Rent in Dublin €1000-€1500/month',
    volume: 2200,
  },
  {
    url: '/rent/dublin/1500-2000',
    title: 'Properties for Rent in Dublin €1500-€2000/month',
    volume: 1900,
  },
  {
    url: '/rent/dublin/2000-2500',
    title: 'Properties for Rent in Dublin €2000-€2500/month',
    volume: 1200,
  },

  // Sale - Location
  { url: '/sale/dublin', title: 'Homes for Sale in Dublin', volume: 4400 },
  { url: '/sale/cork', title: 'Homes for Sale in Cork', volume: 1100 },
  { url: '/sale/galway', title: 'Homes for Sale in Galway', volume: 590 },
  { url: '/sale/dublin-1', title: 'Homes for Sale in Dublin 1', volume: 320 },
  { url: '/sale/dublin-4', title: 'Homes for Sale in Dublin 4', volume: 280 },

  // Sale - First time buyer (special intent)
  {
    url: '/sale/dublin/first-time-buyer',
    title: 'First Time Buyer Homes for Sale in Dublin',
    volume: 1600,
  },
  {
    url: '/sale/cork/first-time-buyer',
    title: 'First Time Buyer Homes for Sale in Cork',
    volume: 320,
  },

  // Share - Location
  { url: '/share/dublin', title: 'Properties to Share in Dublin', volume: 1200 },
  { url: '/share/cork', title: 'Properties to Share in Cork', volume: 280 },
];

/**
 * Generate sitemap entries for pSEO URLs
 */
export const generatepSEOSitemapEntries = () => {
  return popularpSEOTemplates.map((template) => ({
    loc: `https://rentsmart.ie${template.url}`,
    lastmod: new Date().toISOString().split('T')[0],
    changefreq: 'daily',
    priority: template.volume > 2000 ? 0.9 : template.volume > 1000 ? 0.8 : 0.7,
  }));
};

/**
 * Generate internal linking suggestions for a property
 * These can be used to link from property pages to related searches
 */
export const generateRelatedSearchLinks = (
  propertyLocation: string,
  propertyType?: PropertyType,
  amenities?: string[]
): Array<{ url: string; text: string }> => {
  const links: Array<{ url: string; text: string }> = [];

  // Location-based search
  links.push({
    url: generatepSEOUrl('rent', propertyLocation),
    text: `More Properties for Rent in ${propertyLocation}`,
  });

  // Location + type
  if (propertyType) {
    links.push({
      url: generatepSEOUrl('rent', propertyLocation, propertyType.toLowerCase()),
      text: `${propertyType}s for Rent in ${propertyLocation}`,
    });
  }

  // Location + amenity
  if (amenities && amenities.length > 0) {
    const amenity = amenities[0]; // Get first amenity
    const amenitySlug = slugify(amenity);
    links.push({
      url: generatepSEOUrl('rent', propertyLocation, amenitySlug),
      text: `${amenity} Properties in ${propertyLocation}`,
    });
  }

  return links;
};
